import React, { useState, useEffect, useRef } from "react";
import { api, formatApiError, resolveMedia } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { toast } from "sonner";
import { CheckCircle, Warning, ShieldCheck, DownloadSimple, UploadSimple, Eye, Printer, FloppyDisk, X, Camera, Trash, Cake } from "@phosphor-icons/react";
import { Modal } from "./shared";
import PlayerPhotoEditor from "./PlayerPhotoEditor";

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const calcAge = (dob) => {
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  const t = new Date();
  let a = t.getFullYear() - d.getFullYear();
  if (t.getMonth() < d.getMonth() || (t.getMonth() === d.getMonth() && t.getDate() < d.getDate())) a -= 1;
  return a >= 0 && a <= 130 ? a : null;
};
const statusStyle = {
  paid: "bg-[#00E676]/20 text-[#00E676]",
  partial: "bg-[#FFEA00]/20 text-[#FFEA00]",
  unpaid: "bg-[#FF3B30]/20 text-[#FF3B30]",
};
const splitName = (full) => {
  const parts = (full || "").trim().split(/\s+/);
  return { first: parts[0] || "", last: parts.slice(1).join(" ") };
};

export default function PlayerDetailsModal({ player, onClose, onSaved }) {
  const { settings } = useSettings();
  const [p, setP] = useState(player);
  const [teams, setTeams] = useState([]);
  // profile form
  const init = splitName(player.name);
  const [first, setFirst] = useState(init.first);
  const [last, setLast] = useState(init.last);
  const [number, setNumber] = useState(player.number ?? "");
  const [position, setPosition] = useState(player.position || "");
  const [teamId, setTeamId] = useState(player.team_id || "");
  const [status, setStatus] = useState(player.status || "active");
  const [dob, setDob] = useState(player.date_of_birth || "");
  const [email, setEmail] = useState(player.email || "");
  const [phone, setPhone] = useState(player.phone || "");
  const [photoEditor, setPhotoEditor] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  // compliance
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("Zelle");
  const [note, setNote] = useState("");
  const [payDate, setPayDate] = useState("");
  const [busy, setBusy] = useState("");
  const [editFee, setEditFee] = useState(false);
  const [feeVal, setFeeVal] = useState(String(player.effective_fee ?? ""));
  const fileRef = useRef(null);

  useEffect(() => { api.get("/teams").then((r) => setTeams(r.data)).catch(() => {}); }, []);

  const refresh = (data) => { setP(data); onSaved && onSaved(data); };

  const call = async (key, fn, okMsg) => {
    setBusy(key);
    try { const res = await fn(); refresh(res.data); if (okMsg) toast.success(okMsg); return true; }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); return false; }
    finally { setBusy(""); }
  };

  const saveProfile = async () => {
    const name = `${first.trim()} ${last.trim()}`.trim();
    if (!name) { toast.error("First name is required"); return; }
    const num = String(number ?? "").trim() === "" ? null : String(number).trim();
    if (num != null && (!/^[0-9]+$/.test(num) || Number(num) > 9000)) { toast.error("Jersey number must be 00–9000"); return; }
    if (!window.confirm("Save changes to this player? Updates apply everywhere immediately.")) return;
    setSavingProfile(true);
    try {
      const res = await api.put(`/players/${p.id}/profile`, { name, number: num, position, team_id: teamId, status, date_of_birth: dob || "", email: email.trim(), phone: phone.trim() });
      refresh(res.data);
      toast.success("Player profile updated successfully.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingProfile(false); }
  };

  const uploadPhoto = async (file) => {
    setPhotoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(`/players/${p.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      refresh(res.data);
      toast.success("Profile photo updated");
      setPhotoEditor(false);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    finally { setPhotoBusy(false); }
  };

  const removePhoto = async () => {
    if (!window.confirm("Remove this player's profile photo?")) return;
    setPhotoBusy(true);
    try { const res = await api.delete(`/players/${p.id}/photo`); refresh(res.data); toast.success("Photo removed"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setPhotoBusy(false); }
  };

  const recordPayment = async () => {
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt === 0) { toast.error("Enter a payment amount"); return; }
    const ok = await call("pay", () => api.post(`/players/${p.id}/payment`, { amount: amt, method, note, date: payDate }), "Payment recorded");
    if (ok) { setAmount(""); setNote(""); setPayDate(""); }
  };
  const markPaid = () => call("markpaid", () => api.post(`/players/${p.id}/mark-paid`), "Marked paid in full");
  const markUnpaid = () => call("markunpaid", () => api.post(`/players/${p.id}/mark-unpaid`), "Reset to unpaid");
  const saveFee = async () => {
    const v = feeVal.trim() === "" ? null : parseFloat(feeVal);
    if (v !== null && (isNaN(v) || v < 0)) { toast.error("Enter a valid fee"); return; }
    const ok = await call("fee", () => api.post(`/players/${p.id}/fee`, { fee_total: v }), "Fee updated");
    if (ok) setEditFee(false);
  };
  const signWaiver = () => call("sign", () => api.post(`/players/${p.id}/waiver`, { signature: p.name }), "Waiver marked signed");
  const revokeWaiver = () => { if (!window.confirm("Revoke this player's signed waiver? This applies to all their teams.")) return; call("revoke", () => api.post(`/players/${p.id}/waiver/revoke`), "Waiver revoked"); };
  const uploadWaiver = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy("upload");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(`/players/${p.id}/waiver/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      refresh(res.data);
      toast.success("Waiver document uploaded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(""); if (fileRef.current) fileRef.current.value = ""; }
  };
  const viewWaiver = async () => {
    if (p.waiver_doc_url) { window.open(resolveMedia(p.waiver_doc_url), "_blank"); return; }
    try {
      const res = await api.get(`/players/${p.id}/waiver-receipt`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { toast.error("Could not open the waiver"); }
  };
  const printWaiver = async () => {
    try {
      let url;
      if (p.waiver_doc_url) { url = resolveMedia(p.waiver_doc_url); }
      else {
        const res = await api.get(`/players/${p.id}/waiver-receipt`, { responseType: "blob" });
        url = URL.createObjectURL(res.data);
      }
      const w = window.open(url, "_blank");
      if (w) { setTimeout(() => { try { w.focus(); w.print(); } catch (e) { console.warn("Waiver print prompt was blocked:", e); } }, 800); }
    } catch { toast.error("Could not print the waiver"); }
  };
  const downloadWaiver = async () => {
    try {
      const res = await api.get(`/players/${p.id}/waiver-receipt`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `waiver-${(p.name || "player").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Could not download the waiver"); }
  };

  const history = [...(p.payment_history || [])].reverse();
  const inputCls = "w-full bg-[#1A1A1A] border border-white/15 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5722] transition-all";
  const age = calcAge(dob);
  const currentDivision = teams.find((t) => t.id === teamId)?.division || "";
  const ageRule = (settings.division_ages || {})[currentDivision] || null;
  let ageWarn = "";
  if (age != null && ageRule) {
    if (ageRule.min != null && ageRule.min !== "" && age < Number(ageRule.min)) ageWarn = `Age ${age} is below the ${currentDivision} minimum (${ageRule.min}+).`;
    else if (ageRule.max != null && ageRule.max !== "" && age > Number(ageRule.max)) ageWarn = `Age ${age} is above the ${currentDivision} maximum (${ageRule.max}).`;
  }
  const profileMissing = [];
  if (!p.photo_url) profileMissing.push("photo");
  if (!dob) profileMissing.push("date of birth");

  return (
    <Modal open onClose={onClose} title={`${p.name} — Player Details`}>
      <div data-testid="player-details-modal" className="space-y-6">
        {/* Eligibility banner */}
        <div className={`p-4 flex items-start gap-3 ${p.eligible ? "border border-[#00E676]/40 bg-[#00E676]/10" : "border border-[#FF3B30]/40 bg-[#FF3B30]/10"}`} data-testid="eligibility-banner">
          {p.eligible ? <CheckCircle size={22} weight="fill" className="text-[#00E676] shrink-0" /> : <Warning size={22} weight="fill" className="text-[#FF3B30] shrink-0" />}
          <div>
            <div className={`font-bold uppercase tracking-wide text-sm ${p.eligible ? "text-[#00E676]" : "text-[#FF3B30]"}`}>{p.eligible ? "Eligible to Play" : "Not Eligible to Play"}</div>
            {!p.eligible && <div className="text-white/70 text-xs mt-0.5">{p.eligibility_reasons.join(" · ")}</div>}
          </div>
        </div>

        {/* Profile */}
        <div>
          <h4 className="font-display text-xl uppercase mb-3">Profile</h4>

          {/* Profile photo */}
          <div className="flex items-center gap-4 mb-4" data-testid="pd-photo-section">
            <div className="w-24 h-24 bg-[#0A0A0A] border border-white/10 overflow-hidden shrink-0 flex items-center justify-center">
              {p.photo_url
                ? <img src={resolveMedia(p.photo_url)} alt={p.name} className="w-full h-full object-cover" data-testid="pd-photo-preview" />
                : <Camera size={26} className="text-white/20" data-testid="pd-photo-placeholder" />}
            </div>
            <div className="flex flex-col gap-2">
              <button onClick={() => setPhotoEditor(true)} disabled={photoBusy} data-testid="pd-photo-edit-btn" className="flex items-center gap-2 text-xs uppercase font-bold px-3 py-2 border border-white/15 hover:border-[#FF5722] hover:text-[#FF5722] text-white/70 transition-colors disabled:opacity-50"><Camera size={15} /> {p.photo_url ? "Change / Take Photo" : "Take or Upload Photo"}</button>
              {p.photo_url && <button onClick={removePhoto} disabled={photoBusy} data-testid="pd-photo-remove-btn" className="flex items-center gap-2 text-xs uppercase font-bold px-3 py-2 border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 transition-colors disabled:opacity-50"><Trash size={15} /> Remove</button>}
            </div>
          </div>

          {profileMissing.length > 0 && (
            <div className="flex items-start gap-2 border border-[#FFB300]/40 bg-[#FFB300]/10 p-3 mb-4" data-testid="pd-profile-warning">
              <Warning size={18} weight="fill" className="text-[#FFB300] shrink-0 mt-0.5" />
              <p className="text-xs text-white/80">Profile incomplete — missing {profileMissing.join(" and ")}. This won't block play, but please complete it.</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-[10px] uppercase tracking-wide text-white/50 mb-1">First Name</label><input value={first} onChange={(e) => setFirst(e.target.value)} data-testid="pd-first-name" className={inputCls} /></div>
            <div><label className="block text-[10px] uppercase tracking-wide text-white/50 mb-1">Last Name</label><input value={last} onChange={(e) => setLast(e.target.value)} data-testid="pd-last-name" className={inputCls} /></div>
            <div>
              <label className="block text-[10px] uppercase tracking-wide text-white/50 mb-1 flex items-center gap-1"><Cake size={12} /> Date of Birth</label>
              <input type="date" value={dob ? String(dob).slice(0, 10) : ""} onChange={(e) => setDob(e.target.value)} max={new Date().toISOString().slice(0, 10)} data-testid="pd-dob" className={inputCls} />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wide text-white/50 mb-1">Age</label>
              <div className="w-full bg-[#0F0F0F] border border-white/10 px-3 py-2 text-sm tabular-nums" data-testid="pd-age">{age != null ? `${age} yrs` : "—"}</div>
            </div>
            <div><label className="block text-[10px] uppercase tracking-wide text-white/50 mb-1">Jersey Number <span className="text-white/30">(00–9000)</span></label><input type="text" inputMode="numeric" pattern="[0-9]*" maxLength={4} value={number} onChange={(e) => setNumber(e.target.value.replace(/[^0-9]/g, ""))} data-testid="pd-number" className={inputCls} /></div>
            <div><label className="block text-[10px] uppercase tracking-wide text-white/50 mb-1">Position</label><input value={position} onChange={(e) => setPosition(e.target.value)} placeholder="e.g. Guard" data-testid="pd-position" className={inputCls} /></div>
            <div><label className="block text-[10px] uppercase tracking-wide text-white/50 mb-1">Email</label><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="player@email.com" data-testid="pd-email" className={inputCls} /></div>
            <div><label className="block text-[10px] uppercase tracking-wide text-white/50 mb-1">Phone (optional)</label><input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 555-5555" data-testid="pd-phone" className={inputCls} /></div>
            <div><label className="block text-[10px] uppercase tracking-wide text-white/50 mb-1">Team</label>
              <select value={teamId} onChange={(e) => setTeamId(e.target.value)} data-testid="pd-team" className={inputCls}>
                <option value="">— No team —</option>
                {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div><label className="block text-[10px] uppercase tracking-wide text-white/50 mb-1">Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="pd-status" className={inputCls}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          {ageWarn && (
            <div className="flex items-start gap-2 border border-[#FF9500]/40 bg-[#FF9500]/10 p-3 mt-3" data-testid="pd-age-warning">
              <Warning size={18} weight="fill" className="text-[#FF9500] shrink-0 mt-0.5" />
              <p className="text-xs text-white/80">{ageWarn} Eligibility warning only — not blocked.</p>
            </div>
          )}
          <div className="flex gap-2 mt-4">
            <button onClick={saveProfile} disabled={savingProfile} data-testid="pd-save-btn" className="flex items-center gap-2 bg-[#FF5722] hover:bg-[#FF7043] disabled:opacity-50 text-black font-bold uppercase tracking-wide px-5 py-2.5 transition-all"><FloppyDisk size={16} weight="bold" /> {savingProfile ? "Saving…" : "Save Changes"}</button>
            <button onClick={onClose} data-testid="pd-cancel-btn" className="flex items-center gap-2 border border-white/20 hover:border-white text-white/70 font-bold uppercase tracking-wide px-5 py-2.5 transition-all"><X size={16} /> Cancel</button>
          </div>
        </div>

        {/* Payment */}
        <div className="border-t border-white/10 pt-5">
          <h4 className="font-display text-xl uppercase mb-3">Payment</h4>
          <div className="grid grid-cols-3 gap-2 mb-4 text-center">
            <div className="border border-white/10 p-3">
              <div className="text-[10px] uppercase tracking-wide text-white/40 mb-1">League Fee</div>
              {editFee ? (
                <div className="flex items-center gap-1 justify-center">
                  <input value={feeVal} onChange={(e) => setFeeVal(e.target.value)} type="number" min={0} data-testid="fee-input" className="w-16 bg-[#1A1A1A] border border-[#FF5722] px-1 py-0.5 text-sm text-center" />
                  <button onClick={saveFee} disabled={busy === "fee"} data-testid="fee-save-btn" className="text-[#00E676] text-xs font-bold">Save</button>
                </div>
              ) : (
                <button onClick={() => { setFeeVal(String(p.effective_fee)); setEditFee(true); }} data-testid="fee-edit-btn" className="font-display text-2xl tabular-nums mx-auto hover:text-[#FF5722]">{money(p.effective_fee)}</button>
              )}
            </div>
            <div className="border border-white/10 p-3"><div className="text-[10px] uppercase tracking-wide text-white/40 mb-1">Paid</div><div className="font-display text-2xl tabular-nums text-[#00E676]" data-testid="amount-paid">{money(p.amount_paid)}</div></div>
            <div className="border border-white/10 p-3"><div className="text-[10px] uppercase tracking-wide text-white/40 mb-1">Balance</div><div className={`font-display text-2xl tabular-nums ${p.balance > 0 ? "text-[#FF3B30]" : "text-white"}`} data-testid="balance">{money(p.balance)}</div></div>
          </div>
          <div className="flex items-center justify-between mb-4">
            <span className={`text-xs uppercase tracking-wide px-2 py-1 font-bold ${statusStyle[p.payment_status]}`} data-testid="payment-status-badge">{p.payment_status}</span>
            <div className="flex gap-2">
              <button onClick={markPaid} disabled={busy === "markpaid"} data-testid="mark-paid-btn" className="text-xs uppercase font-bold px-3 py-1.5 border border-[#00E676]/40 text-[#00E676] hover:bg-[#00E676]/10 transition-colors">Mark Paid</button>
              <button onClick={markUnpaid} disabled={busy === "markunpaid"} data-testid="mark-unpaid-btn" className="text-xs uppercase font-bold px-3 py-1.5 border border-white/20 text-white/60 hover:bg-white/5 transition-colors">Mark Unpaid</button>
            </div>
          </div>
          <div className="border border-white/10 p-3 space-y-2">
            <div className="text-[10px] uppercase tracking-wide text-white/50">Record a Payment</div>
            <div className="grid grid-cols-3 gap-2">
              <input value={amount} onChange={(e) => setAmount(e.target.value)} type="number" placeholder="Amount" data-testid="payment-amount-input" className="bg-[#1A1A1A] border border-white/15 px-2 py-2 text-sm" />
              <select value={method} onChange={(e) => setMethod(e.target.value)} data-testid="payment-method-select" className="bg-[#1A1A1A] border border-white/15 px-2 py-2 text-sm"><option>Zelle</option><option>Venmo</option><option>Cash</option><option>Card</option><option>Other</option></select>
              <input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} data-testid="payment-date-input" className="bg-[#1A1A1A] border border-white/15 px-2 py-2 text-sm" />
            </div>
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" data-testid="payment-note-input" className="w-full bg-[#1A1A1A] border border-white/15 px-2 py-2 text-sm" />
            <button onClick={recordPayment} disabled={busy === "pay"} data-testid="record-payment-btn" className="w-full bg-[#FF5722] hover:bg-[#FF7043] text-black font-bold uppercase text-xs tracking-wide py-2 transition-colors">Add Payment</button>
          </div>
          {history.length > 0 && (
            <div className="mt-3" data-testid="payment-history">
              <div className="text-[10px] uppercase tracking-wide text-white/50 mb-1">Payment History</div>
              <div className="border border-white/10 max-h-40 overflow-y-auto">
                {history.map((h) => (
                  <div key={h.id} className="flex items-center justify-between px-3 py-2 border-b border-white/5 last:border-0 text-xs">
                    <div><span className={`font-bold tabular-nums ${h.amount < 0 ? "text-[#FF3B30]" : "text-[#00E676]"}`}>{money(h.amount)}</span><span className="text-white/40"> · {h.method || "—"}{h.note ? ` · ${h.note}` : ""}</span></div>
                    <span className="text-white/30">{new Date(h.date).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Waiver */}
        <div className="border-t border-white/10 pt-5">
          <h4 className="font-display text-xl uppercase mb-3 flex items-center gap-2"><ShieldCheck size={20} className="text-[#FF5722]" /> Waiver</h4>
          <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic" onChange={uploadWaiver} className="hidden" data-testid="waiver-upload-input" />
          {p.waiver_signed ? (
            <div className={`border p-3 ${p.waiver_expired ? "border-[#FF9500]/40 bg-[#FF9500]/5" : "border-[#00E676]/30 bg-[#00E676]/5"}`} data-testid="waiver-signed-info">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-sm">
                  {p.waiver_expired
                    ? <div className="text-[#FF9500] font-bold uppercase text-xs tracking-wide" data-testid="waiver-expired-label">Signed — Expired (Re-sign Required)</div>
                    : <div className="text-[#00E676] font-bold uppercase text-xs tracking-wide">Signed</div>}
                  <div className="text-white/60 text-xs mt-0.5">"{p.waiver_signature}" · {p.waiver_method || "signed"} · waiver v{p.waiver_signed_version || "?"}</div>
                  <div className="text-white/50 text-xs mt-0.5" data-testid="waiver-signed-time">{p.waiver_signed_pt ? p.waiver_signed_pt : (p.waiver_signed_at ? new Date(p.waiver_signed_at).toLocaleString() : "")}{p.waiver_tz ? ` (${p.waiver_tz})` : ""}</div>
                  {p.waiver_is_minor && <div className="text-white/50 text-xs mt-0.5" data-testid="waiver-guardian-info">Minor (under 18) · Guardian: {p.waiver_guardian_name || "—"} ("{p.waiver_guardian_signature || "—"}")</div>}
                  {typeof p.waiver_signature_image === "string" && p.waiver_signature_image.startsWith("data:image") && (
                    <img src={p.waiver_signature_image} alt="Signature" data-testid="waiver-signature-image" className="mt-2 h-14 bg-white/90 border border-white/15 px-1" />
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={viewWaiver} data-testid="view-waiver-btn" className="flex items-center gap-1 text-xs uppercase font-bold px-3 py-1.5 border border-white/15 hover:border-[#FF5722] hover:text-[#FF5722] text-white/70 transition-colors"><Eye size={14} /> View</button>
                  <button onClick={downloadWaiver} data-testid="download-waiver-btn" className="flex items-center gap-1 text-xs uppercase font-bold px-3 py-1.5 border border-white/15 hover:border-[#FF5722] hover:text-[#FF5722] text-white/70 transition-colors"><DownloadSimple size={14} /> PDF</button>
                  <button onClick={printWaiver} data-testid="print-waiver-btn" className="flex items-center gap-1 text-xs uppercase font-bold px-3 py-1.5 border border-white/15 hover:border-[#FF5722] hover:text-[#FF5722] text-white/70 transition-colors"><Printer size={14} /> Print</button>
                  <button onClick={() => fileRef.current?.click()} disabled={busy === "upload"} data-testid="replace-waiver-btn" className="flex items-center gap-1 text-xs uppercase font-bold px-3 py-1.5 border border-white/15 hover:border-[#FF5722] hover:text-[#FF5722] text-white/70 transition-colors"><UploadSimple size={14} /> Replace</button>
                  <button onClick={revokeWaiver} disabled={busy === "revoke"} data-testid="revoke-waiver-btn" className="text-xs uppercase font-bold px-3 py-1.5 border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 transition-colors">Revoke</button>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between border border-white/10 p-3 flex-wrap gap-2" data-testid="waiver-pending-info">
              <div className="text-sm">
                <div className="text-[#FFEA00] font-bold uppercase text-xs tracking-wide">Waiver Pending</div>
                {p.balance > 0 && <div className="text-white/50 text-xs mt-0.5">Fee must be paid in full before marking signed.</div>}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => fileRef.current?.click()} disabled={busy === "upload"} data-testid="upload-waiver-btn" className="flex items-center gap-1 text-xs uppercase font-bold px-3 py-1.5 border border-white/15 hover:border-[#FF5722] hover:text-[#FF5722] text-white/70 transition-colors"><UploadSimple size={14} /> {busy === "upload" ? "Uploading…" : "Upload"}</button>
                <button onClick={signWaiver} disabled={busy === "sign" || p.balance > 0} title={p.balance > 0 ? "Pay in full first" : ""} data-testid="admin-sign-waiver-btn" className="text-xs uppercase font-bold px-3 py-1.5 bg-[#FF5722] text-black hover:bg-[#FF7043] disabled:opacity-40 transition-colors">Mark Signed</button>
              </div>
            </div>
          )}
        </div>
      </div>
      {photoEditor && <PlayerPhotoEditor onSave={uploadPhoto} onClose={() => setPhotoEditor(false)} busy={photoBusy} />}
    </Modal>
  );
}
